import { sitePhrasing } from "../../../../Language/languageHandler";
import { AddressInputFieldGroup } from "../../../FormFields/AddressFieldGroup/AddressInputFieldGroup";
import { ArrayEntryFieldType, ArrayFieldInput } from "../../../FormFields/ArrayField/ArrayFieldInput";
import CheckboxInputField from "../../../FormFields/CheckboxInputField/CheckboxInputField";
import RadioInputField from "../../../FormFields/RadioInputField/RadioInputField";
import SelectInputField from "../../../FormFields/SelectInputField/SelectInputField";
import TextInputField from "../../../FormFields/TextInputField/TextInputField";
import { ContactInfoFields } from "../ContactInfoFields";
import { ADDRESS_FIELD_NAMES, blankRegisteredAgentFn, electronicSignatureSublabelGeneral, feiEinStatusOptions, iAgreeText, laborLawPosterAgreementText, mayBeContactedAgreementText, registeredAgentFields, standardAddressFields, stateTransactionAcknowledgmentText, yesOrNoValues } from "../PurchaseConstants";
import { forcedRegistered, forcedStatePrincipal } from "../PurchaseFormHelpers";
import { AnnualReferenceNumberInstructions, TermsAndConditionAcceptText } from "../SharedPurchaseFieldComponents";
import { IndianaAnnualReportTerms } from "./StateSpecificAnnualTerms";

export default function IndianaAnnualReportFields(props: {
  errors: any;
  touched: any;
  values: any;
  setFieldValue: (field: string, value: any) => void;
  selectedProducts: any;
}) {
  const {
    errors,
    touched,
    values,
    setFieldValue,
    selectedProducts = {},
  } = props;
  return (<>

    <ContactInfoFields
      values={values}
      touched={touched}
      errors={errors}
      setFieldValue={setFieldValue}
    />

    <TextInputField
      mask={sitePhrasing.referenceMask}
      fieldName={"referenceNumber"}
      fieldLabel={"Reference Number"}
      fieldSubLabel={undefined}
      customFieldInstructions={() => (<AnnualReferenceNumberInstructions />)}
      errors={errors}
      touched={touched}
      required={true}
      placeholder={"Reference Number"}
      maskGuide={sitePhrasing.referenceMaskGuide}
    />

    <TextInputField
      fieldName={"businessName"}
      fieldLabel={"Complete Business Name"}
      errors={errors}
      touched={touched}
      required={true}
      placeholder={"Complete Business Name"}
    />



    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <RadioInputField
        fieldName={'annualReport.entityStructure'}
        fieldLabel={'Please select which of the following best describes your business entity structure'}
        errors={errors}
        required={true}
        touched={touched}
        options={[
          { value: 'LLC or other', label: 'LLC or other' },
          { value: 'Professional Corporation', label: 'Professional Corporation' },
        ]}
        customClass={'block-radio'}
      />
    </div>

    {values.annualReport.entityStructure === 'Professional Corporation' && (
      <div>
        <h3>Please provide the following required shareholder information: </h3>

        {/* Display "Additional Shareholder" label if there are existing shareholders */}
        <ArrayFieldInput
          fieldName={'annualReport.shareholders'}
          values={values?.annualReport?.shareholders || []}
          blankEntryFn={generateBlankShareholder}
          fields={INDIANA_SHAREHOLDER_FIELDS}
          touched={touched}
          errors={errors}
          removeText={'- Click to remove Additional Shareholder'}
          addText={'+ Click to add Additional Shareholder'}
          minLength={1}
        />
      </div>
    )}
    <TextInputField
      mask={[/\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
      fieldName={'annualReport.feiEin'}
      fieldLabel={'FEI/EIN Number'}
      errors={errors}
      touched={touched}
      required={false}
      placeholder={'FEI/EIN Number'}
    />

    <RadioInputField
      fieldName={"annualReport.feiEinStatus"}
      fieldLabel={"FEI/EIN Number Status"}
      errors={errors}
      required={true}
      touched={touched}
      options={feiEinStatusOptions}
      customClass={"block-radio"}
    />
    <div className="field-note">
      If you have not yet obtained your EIN Federal Tax ID, you may do so now by{" "}<a href="/" target="_blank" className="link-text">clicking here</a>.
    </div>

    <RadioInputField
      fieldName={"annualReport.providesHealthCareAcceptsReimbursement"}
      fieldLabel={"Does this entity provide health care services and accept commercial health insurance reimbursement?"}
      errors={errors}
      required={true}
      touched={touched}
      options={yesOrNoValues}
    // customClass={"block-radio"}
    />

    {values.annualReport.providesHealthCareAcceptsReimbursement === "yes" && (
      <>
        <RadioInputField
          fieldName={"annualReport.isHealthCareEntity"}
          fieldLabel={"Is this entity a hospital, insurer, pharmacy health benefit manager, or third-party administrator?"}
          errors={errors}
          required={true}
          touched={touched}
          options={yesOrNoValues}
          onChange={(e: any) => {
            if (e.target.value === "yes") {
              // Clear ownershipAndControlInterests if user selects "yes"
              setFieldValue('annualReport.ownershipAndControlInterests', []);
            } else {
              // Initialize ownershipAndControlInterests with one blank entry if user selects "no" and it's currently empty
              if (!values.annualReport.ownershipAndControlInterests || values.annualReport.ownershipAndControlInterests.length === 0) {
                setFieldValue('annualReport.ownershipAndControlInterests', [generateBlankOwnershipInterest()]);
              }
            }
            setTimeout(() => {
              setFieldValue('annualReport.isHealthCareEntity', e.target.value);
            }, 0);
          }}
        // customClass={"block-radio"}
        />

        {values.annualReport.isHealthCareEntity === "no" && (
          <>
            <div style={{ marginBottom: '15px' }}>
              Provide the name of each person or entity that holds either an ownership interest of at least 5%, a controlling interest, or an interest as a private equity partner in your healthcare entity.
            </div>
            <div className="error-sub-label" style={{ color: 'red', marginBottom: '10px' }}>
              P.O. Boxes are not accepted
            </div>
            <ArrayFieldInput
              fieldName={"annualReport.ownershipAndControlInterests"}
              values={values?.annualReport?.ownershipAndControlInterests || []}
              blankEntryFn={generateBlankOwnershipInterest}
              fields={OWNERSHIP_INTEREST_FIELDS}
              touched={touched}
              errors={errors}
              removeText={"- Click to remove entry"}
              addText={"+ Click to add entry"}
              minLength={1}
            />

            <RadioInputField
              fieldName={"annualReport.isMedicaidProvider"}
              fieldLabel={"Is your health care entity a Medicaid provider?"}
              errors={errors}
              required={true}
              touched={touched}
              options={yesOrNoValues}
            // customClass={"block-radio"}
            />
          </>
        )}
      </>
    )}

    <h3>Do you need to change any of the following information?</h3>

    <RadioInputField
      fieldName={
        "annualReport.informationChange.emailAddress.emailAddressChange"
      }
      fieldLabel={"E-Mail on file?"}
      errors={errors}
      required={true}
      touched={touched}
      options={yesOrNoValues}
    />
    {values.annualReport.informationChange.emailAddress.emailAddressChange === "yes" && (
      <TextInputField
        fieldName={"annualReport.informationChange.emailAddress.newEmailAddress"}
        fieldLabel={"Enter your new COMPLETE e-mail address"}
        errors={errors}
        touched={touched}
        required={true}
        placeholder={"Enter your new COMPLETE e-mail address"}
      />
    )}

    <RadioInputField
      fieldName={"annualReport.informationChange.principalAddress.changed"}
      fieldLabel={"Principal Address?"}
      errors={errors}
      required={true}
      touched={touched}
      options={yesOrNoValues}
    />
    {values.annualReport.informationChange.principalAddress.changed === "yes" && (
      <AddressInputFieldGroup
        fieldName={"annualReport.informationChange.principalAddress.newAddress"}
        errors={errors}
        touched={touched}
        fieldMessages={{
          state: forcedStatePrincipal[sitePhrasing.state] ? `Your principal address must be located in ${sitePhrasing.state}.` : ``,
        }}
        disableFields={{
          state: forcedStatePrincipal[sitePhrasing.state] ? true : false,
        }}
      />
    )}

    <RadioInputField
      fieldName={"annualReport.informationChange.registeredAgent.agentChange"}
      fieldLabel={"Registered Agent?"}
      errors={errors}
      required={true}
      touched={touched}
      options={yesOrNoValues}
    />
    {values.annualReport.informationChange.registeredAgent.agentChange === "yes" && (
      <>
        <RadioInputField
          fieldName={"annualReport.informationChange.registeredAgent.agentChangeType"}
          fieldLabel={"What type of change?"}
          errors={errors}
          required={true}
          touched={touched}
          customClass={`full-width`}
          options={[
            { value: "address", label: "Change my current Registered Agent's address only", },
            { value: "nameAddress", label: "Change my Registered Agent's name and address", },
          ]}
        />

        {values.annualReport.informationChange.registeredAgent.agentChangeType === "nameAddress" && (
          <>
            <RadioInputField
              fieldName={"annualReport.informationChange.registeredAgent.agentType"}
              fieldLabel={"My Registered Agent is"}
              errors={errors}
              required={true}
              touched={touched}
              options={[
                { value: "individual", label: "Individual" },
                { value: "business", label: "Business" },
              ]}
            />
          </>
        )}

        {values.annualReport.informationChange.registeredAgent.agentType === "individual" && (
          <>
            <TextInputField
              fieldName={"annualReport.informationChange.registeredAgent.firstName"}
              fieldLabel={"First Name"}
              errors={errors}
              touched={touched}
              required={true}
              placeholder={"First Name"}
            />
            <TextInputField
              fieldName={"annualReport.informationChange.registeredAgent.lastName"}
              fieldLabel={"Last Name"}
              errors={errors}
              touched={touched}
              required={true}
              placeholder={"Last Name"}
            />
            <TextInputField
              fieldName={"annualReport.informationChange.registeredAgent.email"}
              fieldLabel={"Email"}
              errors={errors}
              touched={touched}
              required={true}
              placeholder={"Email"}
            />
          </>
        )}

        {values.annualReport.informationChange.registeredAgent.agentType === "business" && (
          <>
            <TextInputField
              fieldName={"annualReport.informationChange.registeredAgent.businessName"}
              fieldLabel={"Business Name"}
              errors={errors}
              touched={touched}
              required={true}
              placeholder={"Business Name"}
            />
            <div className="field-note">
              * Business must be active and registered in the state of
              Indiana. Please type business name exactly as it appears on the
              Indiana Secretary of State to avoid errors and delays in your
              order. It is possible we may contact you if we are unable to locate the business.
            </div>
          </>
        )}

        {(values.annualReport.informationChange.registeredAgent.agentChangeType === "address" ||
          values.annualReport.informationChange.registeredAgent.agentType === "individual") && (
            <AddressInputFieldGroup
              fieldName={"annualReport.informationChange.registeredAgent.newAddress"}
              errors={errors}
              touched={touched}
              fieldMessages={{
                state: forcedRegistered[sitePhrasing.state] ? `Your registered agent must be located in ${sitePhrasing.state}.` : ``,
              }}
              disableFields={{
                state: forcedRegistered[sitePhrasing.state] ? true : false,
              }}
            />
          )}
      </>
    )}

    <RadioInputField
      fieldName={"annualReport.informationChange.authorizedPerson.authorizedPersonChange"}
      fieldLabel={"Governing Person(s)?"}
      errors={errors}
      required={true}
      touched={touched}
      options={yesOrNoValues}
    />
    {values.annualReport.informationChange.authorizedPerson.authorizedPersonChange === "yes" && (
      <>
        <RadioInputField
          fieldName={"annualReport.informationChange.authorizedPerson.removingAgents"}
          fieldLabel={`Are you removing any governing person(s)?`}
          errors={errors}
          required={true}
          touched={touched}
          options={yesOrNoValues}
        />
        {values.annualReport.informationChange.authorizedPerson.removingAgents === "yes" && (
          <ArrayFieldInput
            fieldName={"annualReport.informationChange.authorizedPerson.removedAgents"}
            values={values.annualReport.informationChange.authorizedPerson.removedAgents}
            blankEntryFn={blankRegisteredAgentFn}
            fields={registeredAgentFields}
            touched={touched}
            errors={errors}
            removeText={"- Click to remove entry"}
            addText={"+ Click to add an entry"}
          />
        )}

        <RadioInputField
          fieldName={"annualReport.informationChange.authorizedPerson.addingAgents"}
          fieldLabel={`Are you adding any governing person(s)?`}
          errors={errors}
          required={true}
          touched={touched}
          options={yesOrNoValues}
        />
        {values.annualReport.informationChange.authorizedPerson.addingAgents === "yes" && (
          <ArrayFieldInput
            fieldName={"annualReport.informationChange.authorizedPerson.addedAgents"}
            values={values.annualReport.informationChange.authorizedPerson.addedAgents}
            blankEntryFn={blankRegisteredAgentFn}
            fields={registeredAgentFields}
            touched={touched}
            errors={errors}
            removeText={"- Click to remove entry"}
            addText={"+ Click to add an entry"}
          />
        )}

        <RadioInputField
          fieldName={"annualReport.informationChange.authorizedPerson.changingAddresses"}
          fieldLabel={`Change address for existing governing person(s)?`}
          errors={errors}
          required={true}
          touched={touched}
          options={yesOrNoValues}
        />

        {values.annualReport.informationChange.authorizedPerson.changingAddresses === "yes" && (
          <ArrayFieldInput
            fieldName={"annualReport.informationChange.authorizedPerson.changedAddresses"}
            values={values.annualReport.informationChange.authorizedPerson.changedAddresses}
            blankEntryFn={blankRegisteredAgentFn}
            fields={registeredAgentFields}
            touched={touched}
            errors={errors}
            removeText={"- Click to remove entry"}
            addText={"+ Click to add an entry"}
          />
        )}
      </>
    )}

    <div className="terms-and-conditions-container">
      <IndianaAnnualReportTerms />
      <br /><br />
      {iAgreeText}
      <br /><br />
      {selectedProducts["laborLawPoster"] && (
        <>
          {laborLawPosterAgreementText}
          <br /><br />
        </>
      )}
      {stateTransactionAcknowledgmentText}
      <br /><br />
      <TermsAndConditionAcceptText />
    </div>

    <CheckboxInputField
      fieldName={'termAndConditionAgree'}
      fieldLabel={`I've read and accept the terms and conditions.`}
      touched={touched}
      errors={errors}
      onChange={(e: any) => {
        const checkedVal = e.target.checked ? true : false;
        setFieldValue('annualReport.annualAgree', checkedVal)
        setFieldValue('annualReport.tellingTruth', checkedVal)
        setFieldValue('annualReport.iAmAuthorized', checkedVal)
        setFieldValue('iAgree', checkedVal)
        setTimeout(() => {
          setFieldValue('termAndConditionAgree', checkedVal)
        }, 0)

      }}
    />

    <br />

    <TextInputField
      fieldName={"annualReport.electronicSignature"}
      fieldLabel={"Name of Individual Signing - Electronic Signature"}
      fieldSubLabel={electronicSignatureSublabelGeneral}
      fieldSubLabelCustomClass={'red-text'}
      errors={errors}
      touched={touched}
      required={true}
      placeholder={"Electronic Signature"}
      customClass={"signature-font"}
    />

    <SelectInputField
      fieldName={"annualReport.title"}
      fieldLabel={"Title"}
      errors={errors}
      touched={touched}
      required={true}
      placeholder={"- Please Select -"}
      options={[
        { value: "member", label: "Member" },
        { value: "manager", label: "Manager" },
        { value: "registeredAgent", label: "Registered Agent" },
        { value: "cpa", label: "CPA" },
        { value: "authorizedAgent", label: "Authorized Agent" },
        { value: "legalRepresentative", label: "Legal Representative" },
      ]}
    />
  </>
  );
}

const generateBlankShareholder = () => ({
  firstName: '',
  lastName: '',
  profession: '',
  indianaProfessionalLicenseNumber: '',
  country: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
})

const generateBlankOwnershipInterest = () => ({
  name: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  ownershipStake: '',
})

const INDIANA_SHAREHOLDER_FIELDS: ArrayEntryFieldType[] = [
  {
    fieldName: 'firstName',
    fieldLabel: 'First Name',
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'lastName',
    fieldLabel: 'Last Name',
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'profession',
    fieldLabel: 'Profession',
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'indianaProfessionalLicenseNumber',
    fieldLabel: 'Indiana Professional License Number',
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'country',
    fieldLabel: 'Country',
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'address',
    fieldLabel: ADDRESS_FIELD_NAMES.address,
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'address2',
    fieldLabel: ADDRESS_FIELD_NAMES.address2,
    fieldType: 'text',
  },
  {
    fieldName: 'city',
    fieldLabel: ADDRESS_FIELD_NAMES.city,
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'state',
    fieldLabel: ADDRESS_FIELD_NAMES.state,
    fieldType: 'text',
    required: true,
  },
  {
    fieldName: 'zip',
    fieldLabel: ADDRESS_FIELD_NAMES.zip,
    fieldType: 'text',
    required: true,
  }
]

const OWNERSHIP_INTEREST_FIELDS: ArrayEntryFieldType[] = [
  {
    fieldName: 'name',
    fieldLabel: 'Name',
    fieldType: 'text',
    placeholder: 'Name',
    required: true,
  },
  ...standardAddressFields,
  // {
  //   fieldName: 'address',
  //   fieldLabel: 'Street Address 1',
  //   fieldType: 'text',
  //   required: true,
  // },
  // {
  //   fieldName: 'address2',
  //   fieldLabel: 'Street Address 2 (apt, suite, unit, building, floor, etc.)',
  //   fieldType: 'text',
  // },
  // {
  //   fieldName: 'city',
  //   fieldLabel: 'City',
  //   fieldType: 'text',
  //   required: true,
  // },
  // {
  //   fieldName: 'state',
  //   fieldLabel: 'State',
  //   fieldType: 'text',
  //   required: true,
  // },
  // {
  //   fieldName: 'zip',
  //   fieldLabel: 'Zip Code',
  //   fieldType: 'text',
  //   required: true,
  // },
  {
    fieldName: 'ownershipStake',
    fieldLabel: 'Ownership Stake (%)',
    placeholder: 'Ownership Stake (%)',
    fieldType: 'number',
    required: true,
  },
]